import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Loader2, Users, Mail, AlertCircle, CheckCircle2, XCircle, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [shareCalls, setShareCalls] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/invitations/verify/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || 'Invitation not found');
        }
        setInvite(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      navigate(`/auth?invite=${token}&email=${encodeURIComponent(invite.email)}&mode=${invite.user_has_account ? 'login' : 'signup'}`);
      return;
    }
    if (user?.email?.toLowerCase() !== invite.email.toLowerCase()) {
      toast.error(`This invitation is for ${invite.email}. Please sign in with that email.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/invitations/${token}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ share_existing_calls: shareCalls }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Failed to accept invitation');
      toast.success(body.message || 'Welcome to the team');
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!confirm('Decline this invitation? This cannot be undone.')) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/invitations/${token}/decline`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to decline');
      toast.success('Invitation declined');
      navigate('/');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <InviteShell>
        <div className="text-center" data-testid="invite-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Invitation link invalid</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline">Back to Ludy</Button>
          </Link>
        </div>
      </InviteShell>
    );
  }

  if (invite.status !== 'pending') {
    const icons = {
      accepted: <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />,
      declined: <XCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />,
      expired: <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />,
      revoked: <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />,
    };
    const messages = {
      accepted: 'You have already accepted this invitation.',
      declined: 'This invitation was declined.',
      expired: 'This invitation has expired. Ask your manager to send a new one.',
      revoked: 'This invitation was revoked.',
    };
    return (
      <InviteShell>
        <div className="text-center" data-testid={`invite-status-${invite.status}`}>
          {icons[invite.status]}
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2 capitalize">
            Invitation {invite.status}
          </h1>
          <p className="text-muted-foreground mb-6">{messages[invite.status]}</p>
          <Link to="/">
            <Button variant="outline">Back to Ludy</Button>
          </Link>
        </div>
      </InviteShell>
    );
  }

  const emailMismatch = isAuthenticated && user?.email?.toLowerCase() !== invite.email.toLowerCase();

  return (
    <InviteShell>
      <div data-testid="invite-landing">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-5">
          <Users className="w-6 h-6 text-primary" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-foreground text-center mb-2">
          Join {invite.team_name || 'the team'}
        </h1>
        <p className="text-muted-foreground text-center mb-6">
          <strong className="text-foreground">{invite.inviter_name}</strong> invited <strong className="text-foreground">{invite.email}</strong> to join on Ludy.
        </p>

        {emailMismatch && (
          <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 mb-5">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              You are signed in as <strong>{user.email}</strong>, but this invitation is for <strong>{invite.email}</strong>.
              Please sign out and sign in with {invite.email}.
            </p>
          </div>
        )}

        {isAuthenticated && !emailMismatch && invite.existing_calls_count > 0 && (
          <label
            className="flex items-start gap-3 p-4 rounded-lg border border-border bg-secondary/30 mb-5 cursor-pointer hover:bg-secondary/50 transition-colors"
            data-testid="share-calls-option"
          >
            <Checkbox
              checked={shareCalls}
              onCheckedChange={setShareCalls}
              className="mt-0.5"
              data-testid="share-calls-checkbox"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                Share my existing calls with the manager
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                You already have <strong>{invite.existing_calls_count}</strong> call{invite.existing_calls_count !== 1 ? 's' : ''} on your account.
                If unchecked, only calls created after today will be visible to <strong>{invite.inviter_name}</strong>.
              </p>
            </div>
          </label>
        )}

        {!isAuthenticated && (
          <div className="flex items-start gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5 mb-5">
            <Mail className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              {invite.user_has_account
                ? 'Sign in to your existing account to accept this invitation.'
                : 'Create your Ludy account to accept this invitation.'}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleAccept}
            disabled={submitting || emailMismatch}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="invite-accept-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {!isAuthenticated
              ? (invite.user_has_account ? 'Sign in & accept' : 'Create account & accept')
              : 'Accept invitation'}
          </Button>
          <Button
            onClick={handleDecline}
            disabled={submitting}
            variant="outline"
            data-testid="invite-decline-btn"
          >
            Decline
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-5">
          Invitation expires {new Date(invite.expires_at).toLocaleDateString()}
        </p>
      </div>
    </InviteShell>
  );
}

function InviteShell({ children }) {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(14,89,68,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src="/ludy-wordmark.png" alt="Ludy" className="h-10" />
        </div>
        <Card className="bg-card border-border/60">
          <CardContent className="p-8">{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}
